/*
  Product page behaviour: media gallery, variant selection and add to cart.

  <product-gallery>  one visible slide, thumbnails, previous/next, swipe, zoom dialog
  <product-info>     variant picker, live price and availability, add to cart

  The variant data comes from a JSON script block inside <product-info>. Price,
  availability and SKU are re-rendered by Liquid through the Section Rendering
  API, so money formatting and currency (Markets) are never done in JS.
*/
import { fetchDocument, announce } from '@theme/utils';
import { addToCart, openCartDrawer } from '@theme/cart';

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.lightbox = this.querySelector('[data-gallery-lightbox]');

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.select(thumb.dataset.mediaId)));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));

    this.querySelectorAll('[data-gallery-zoom]').forEach((button) =>
      button.addEventListener('click', () => {
        if (!this.lightbox) return;
        const image = this.lightbox.querySelector('img');
        image.src = button.dataset.zoomSrc;
        image.alt = button.dataset.zoomAlt || '';
        this.lightbox.showModal();
      })
    );

    // Horizontal swipe on touch screens.
    let startX = null;
    const stage = this.querySelector('[data-gallery-stage]');
    stage?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') startX = event.clientX;
    });
    stage?.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const delta = event.clientX - startX;
      startX = null;
      if (Math.abs(delta) > 40) this.step(delta < 0 ? 1 : -1);
    });
  }

  get currentIndex() {
    return Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
  }

  step(direction) {
    const next = (this.currentIndex + direction + this.slides.length) % this.slides.length;
    this.select(this.slides[next].dataset.mediaId);
  }

  select(mediaId) {
    if (!mediaId) return;
    const match = this.slides.find((slide) => slide.dataset.mediaId === String(mediaId));
    if (!match) return;
    this.slides.forEach((slide) => {
      slide.hidden = slide !== match;
    });
    this.thumbs.forEach((thumb) => thumb.setAttribute('aria-current', String(thumb.dataset.mediaId === String(mediaId))));
    const position = this.querySelector('[data-gallery-position]');
    if (position) position.textContent = `${this.slides.indexOf(match) + 1} / ${this.slides.length}`;
  }
}

class ProductInfo extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[data-product-json]');
    if (!data) return;
    this.product = JSON.parse(data.textContent);
    this.form = this.querySelector('form[data-product-form]');
    this.idInput = this.form?.querySelector('input[name="id"]');
    this.abort = null;

    this.addEventListener('change', (event) => {
      if (event.target.closest('[data-option-group]')) this.onOptionChange();
    });
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.markAvailability();
    this.watchStickyBar();
  }

  /**
   * Shows the mobile sticky add to cart bar once the main button has scrolled
   * above the screen. A scroll check is used instead of IntersectionObserver,
   * which only fires when visibility changes and so misses fast scrolls and
   * restored scroll positions that jump straight past the button.
   */
  watchStickyBar() {
    const bar = this.querySelector('[data-sticky-atc]');
    const buy = this.querySelector('.product__buy');
    if (!bar || !buy) return;
    let queued = false;
    const update = () => {
      queued = false;
      bar.hidden = buy.getBoundingClientRect().bottom > 0;
    };
    this.stickyScroll = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', this.stickyScroll, { passive: true });
    window.addEventListener('resize', this.stickyScroll, { passive: true });
    update();
  }

  disconnectedCallback() {
    if (!this.stickyScroll) return;
    window.removeEventListener('scroll', this.stickyScroll);
    window.removeEventListener('resize', this.stickyScroll);
  }

  groups() {
    return [...this.querySelectorAll('[data-option-group]')];
  }

  selectedOptions() {
    return this.groups().map((group) => {
      const checked = group.querySelector('input:checked');
      return checked ? checked.value : group.querySelector('select')?.value;
    });
  }

  findVariant(options) {
    return this.product.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  /** Marks option values that lead to a sold-out or non-existent variant. */
  markAvailability() {
    const selected = this.selectedOptions();
    this.groups().forEach((group, index) => {
      group.querySelectorAll('[data-option-value]').forEach((control) => {
        const candidate = selected.map((value, i) => (i === index ? control.value : value));
        const variant = this.findVariant(candidate);
        const state = !variant ? 'unavailable' : variant.available ? 'available' : 'sold-out';
        control.closest('[data-option-choice]')?.setAttribute('data-state', state);
        if (control.tagName === 'OPTION') control.textContent = control.dataset.label + (state === 'available' ? '' : ` (${control.dataset[state === 'sold-out' ? 'soldOutLabel' : 'unavailableLabel']})`);
      });
    });
  }

  async onOptionChange() {
    this.markAvailability();
    const variant = this.findVariant(this.selectedOptions());
    const submit = this.querySelector('[data-add-to-cart]');

    if (!variant) {
      if (submit) {
        submit.disabled = true;
        submit.querySelector('span').textContent = submit.dataset.unavailableLabel;
      }
      return;
    }

    if (this.idInput) this.idInput.value = variant.id;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(null, '', url);

    if (variant.featured_media?.id) this.closest('[data-product-section]')?.querySelector('product-gallery')?.select(variant.featured_media.id);

    this.abort?.abort();
    this.abort = new AbortController();
    try {
      const doc = await fetchDocument(`${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.sectionId}`, { signal: this.abort.signal });
      this.querySelectorAll('[data-product-update]').forEach((element) => {
        const next = doc.querySelector(`[data-product-update="${CSS.escape(element.dataset.productUpdate)}"]`);
        if (next) element.innerHTML = next.innerHTML;
      });
      announce(this.querySelector('[data-product-update^="price"]')?.textContent.replace(/\s+/g, ' ').trim());
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }

  async onSubmit(event) {
    const button = this.querySelector('[data-add-to-cart]');
    if (button?.disabled) {
      event.preventDefault();
      return;
    }
    // Without a drawer the form posts normally and lands on the cart page.
    if (!document.getElementById('CartDrawer')) return;
    event.preventDefault();

    const error = this.querySelector('[data-product-error]');
    if (error) error.hidden = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      await addToCart(new FormData(this.form));
      openCartDrawer();
    } catch (failure) {
      if (error) {
        error.textContent = failure.message;
        error.hidden = false;
      }
      announce(failure.message);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

/** Loads related products when the section nears the viewport. */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    const load = async () => {
      try {
        const doc = await fetchDocument(this.dataset.url);
        const next = doc.querySelector('product-recommendations');
        if (next && next.querySelector('.product-grid')) this.innerHTML = next.innerHTML;
        this.dataset.loaded = 'true';
      } catch (error) {
        console.error(error);
      }
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      }, { rootMargin: '400px' });
      observer.observe(this);
    } else {
      load();
    }
  }
}

if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
